import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Mail, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, type CommsSettings } from "@/lib/api";
import { textOn } from "@logiful/domain";
import { intlLocale, t, msg } from "@/lib/i18n";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");
const KIND: Record<string, string> = { address_clarification: "Adresse prüfen", address_reminder: "Erinnerung Adresse", delay_notice: "Lieferverzögerung" };
const STATUS: Record<string, { label: string; tone: "success" | "warning" | "danger" | "neutral" | "info" }> = {
  pending: { label: msg("geplant"), tone: "info" },
  sending: { label: msg("wird gesendet"), tone: "info" },
  sent: { label: msg("gesendet"), tone: "success" },
  suppressed: { label: msg("nicht nötig"), tone: "neutral" },
  failed: { label: msg("fehlgeschlagen"), tone: "danger" },
};

function Switch({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} className="flex items-start gap-2.5 text-left disabled:opacity-60">
      <span className={cx("mt-0.5 w-8 h-[18px] shrink-0 rounded-full p-0.5 transition-colors", checked ? "bg-ink" : "bg-border-strong")}><span className={cx("block size-3.5 rounded-full bg-surface transition-transform", checked && "translate-x-3.5")} /></span>
      <span className="text-[13px]"><span className="font-medium">{t(label)}</span>{hint && <span className="block text-[12px] text-fg-subtle">{t(hint)}</span>}</span>
    </button>
  );
}

/** Kundenkommunikation: Mails in eurem Namen an eure Endkunden – Einstellungen, Vorschau, Protokoll */
export function CustomerCommsPage() {
  const { tenantId, role } = useTenant();
  const editable = canManage(role);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["customer-comms", tenantId], queryFn: () => api.customerComms.get(tenantId) });
  const log = useQuery({ queryKey: ["customer-comms-log", tenantId], queryFn: () => api.customerComms.messages(tenantId), refetchInterval: 60_000 });
  const [s, setS] = useState<CommsSettings | null>(null);
  const [preview, setPreview] = useState<"address" | "delay" | null>(null);
  useEffect(() => { if (q.data && !s) setS(q.data.settings); }, [q.data, s]);
  const pv = useQuery({ queryKey: ["customer-comms-preview", tenantId, s], queryFn: () => api.customerComms.preview(tenantId, s!), enabled: !!s && !!preview });
  const save = useMutation({
    mutationFn: () => api.customerComms.save(tenantId, s!),
    onSuccess: () => { toast.success("Gespeichert"); void qc.invalidateQueries({ queryKey: ["customer-comms", tenantId] }); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const test = useMutation({
    mutationFn: (kind: "address" | "delay") => api.customerComms.test(tenantId, kind),
    onSuccess: (r) => toast.success("Testmail gesendet", { description: `an ${r.sentTo}` }),
    onError: (e) => toast.error("Nicht gesendet", { description: errMsg(e) }),
  });
  if (!q.data || !s) return <div className="px-4 md:px-8 py-8"><Skeleton className="h-80 w-full" /></div>;
  const dirty = JSON.stringify(s) !== JSON.stringify(q.data.settings);
  const set = (patch: Partial<CommsSettings>) => setS({ ...s, ...patch });

  return (
    <div className="pb-16">
      <PageHeader title={t("Kundenkommunikation")} description={t("Mails in eurem Namen an eure Endkunden – automatisch, wenn es etwas zu klären gibt. Nur Service-Mails, keine Werbung.")} />
      <div className="px-4 md:px-8 space-y-4 max-w-4xl">
        {!q.data.masterEnabled && (
          <div className="rounded-xl border border-info/25 bg-info-soft/60 px-4 py-3 text-[13px]">
            <b>{t("Noch nicht aktiv:")}</b> {t("Logiful gibt den Versand an Endkunden in Kürze frei. Ihr könnt schon alles einstellen und euch Testmails schicken – bis zur Freigabe geht nichts an eure Kunden.")}
          </div>
        )}

        <Card className="p-5 space-y-4">
          <h2 className="font-semibold text-[14px]">{t("Erscheinungsbild")}</h2>
          <BrandEditor editable={editable} hasLogo={q.data.hasLogo} color={s.brandColor} footer={s.footer} onColor={(c) => set({ brandColor: c })} onFooter={(f) => set({ footer: f })} />
        </Card>

        <Card className="p-5 space-y-4">
          <h2 className="font-semibold text-[14px]">{t("Absender")}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted">{t("Absendername")}</span>
              <Input className="mt-1" value={s.senderName ?? ""} placeholder={q.data.defaultSenderName} disabled={!editable} onChange={(e) => set({ senderName: e.target.value || null })} />
              <span className="text-[11.5px] text-fg-subtle">{t("So steht ihr im Postfach eurer Kunden.")}</span>
            </label>
            <label className="block">
              <span className="text-[12px] font-medium text-fg-muted">{t("Antworten an")}</span>
              <Input className="mt-1" type="email" value={s.replyTo ?? ""} placeholder={t("service@euer-shop.de")} disabled={!editable} onChange={(e) => set({ replyTo: e.target.value || null })} />
              <span className="text-[11.5px] text-fg-subtle">{t("Antworten eurer Kunden landen direkt bei euch.")}</span>
            </label>
          </div>
          <label className="block">
            <span className="text-[12px] font-medium text-fg-muted">{t("Grußformel")}</span>
            <textarea rows={2} className="mt-1 w-full rounded-lg border border-border bg-surface p-2.5 text-[13px]" value={s.signature ?? ""} placeholder={t("Viele Grüße\n{v}", { v: s.senderName ?? q.data.defaultSenderName })} disabled={!editable} onChange={(e) => set({ signature: e.target.value || null })} />
          </label>
        </Card>

        <Card className="p-5 space-y-3">
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <Switch checked={s.addressClarification.enabled} disabled={!editable} onChange={(v) => set({ addressClarification: { ...s.addressClarification, enabled: v } })} label={t("Adressklärung durch eure Kunden")} hint={t("Hängt ein Auftrag wegen der Adresse fest, bekommt euer Kunde nach 15 Minuten eine Mail mit Link: Adresse prüfen, korrigieren oder bestätigen – der Auftrag läuft danach automatisch weiter.")} />
            </div>
            <Button size="sm" variant="ghost" icon={<Eye className="size-3.5" />} onClick={() => setPreview(preview === "address" ? null : "address")}>{t("Vorschau")}</Button>
          </div>
          {s.addressClarification.enabled && (
            <div className="pl-10 space-y-3">
              <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t("Versand")}>
                {([["auto", msg("Automatisch"), msg("bei jedem Adressproblem nach 15 Minuten")], ["manual", msg("Nur manuell"), msg("nur wenn ihr im Auftrag „Kunden fragen“ wählt")]] as const).map(([v, label, hint]) => (
                  <button key={v} type="button" role="radio" aria-checked={s.addressClarification.mode === v} disabled={!editable} onClick={() => set({ addressClarification: { ...s.addressClarification, mode: v } })} className={cx("rounded-xl border px-3 py-2 text-left text-[13px] min-w-[200px] flex-1", s.addressClarification.mode === v ? "border-fg bg-surface-2" : "border-border hover:bg-surface-2")}>
                    <span className="font-medium">{t(label)}</span>
                    <span className="block text-[12px] text-fg-subtle">{t(hint)}</span>
                  </button>
                ))}
              </div>
              <label className="flex flex-wrap items-center gap-2 text-[13px]">
                {t("Erinnerung nach")}
                <select disabled={!editable} value={s.addressClarification.reminderHours} onChange={(e) => set({ addressClarification: { ...s.addressClarification, reminderHours: Number(e.target.value) } })} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
                  {[0, 12, 24, 48, 72].map((h) => <option key={h} value={h}>{h ? t("{h} Stunden", { h }) : t("keine Erinnerung")}</option>)}
                </select>
                <span className="text-fg-subtle text-[12px]">{t("· ohne Antwort bleibt der Auftrag im Handlungsbedarf")}</span>
              </label>
              <label className="block">
                <span className="text-[12px] font-medium text-fg-muted">{t("Eigener Einleitungstext (optional)")}</span>
                <textarea rows={2} className="mt-1 w-full rounded-lg border border-border bg-surface p-2.5 text-[13px]" value={s.addressClarification.intro ?? ""} placeholder={t("Standardtext verwenden")} disabled={!editable} onChange={(e) => set({ addressClarification: { ...s.addressClarification, intro: e.target.value || null } })} />
              </label>
            </div>
          )}
          {preview === "address" && <MailPreview html={pv.data?.address.html} subject={pv.data?.address.subject} />}
        </Card>

        <Card className="p-5 space-y-3">
          <div className="flex flex-wrap items-start gap-3">
            <div className="min-w-0 flex-1">
              <Switch checked={s.delayNotice.enabled} disabled={!editable} onChange={(v) => set({ delayNotice: { ...s.delayNotice, enabled: v } })} label={t("Hinweis bei Lieferverzögerung")} hint={t("Wartet ein Auftrag auf Ware, erfährt euer Kunde es – mit voraussichtlichem Versandtermin, sobald eine Anlieferung angemeldet ist. Weniger „Wo bleibt meine Bestellung?“-Anfragen.")} />
            </div>
            <Button size="sm" variant="ghost" icon={<Eye className="size-3.5" />} onClick={() => setPreview(preview === "delay" ? null : "delay")}>{t("Vorschau")}</Button>
          </div>
          {s.delayNotice.enabled && (
            <div className="pl-10 space-y-3">
              <label className="flex flex-wrap items-center gap-2 text-[13px]">
                {t("Melden, wenn der Auftrag länger wartet als")}
                <select disabled={!editable} value={s.delayNotice.afterHours} onChange={(e) => set({ delayNotice: { ...s.delayNotice, afterHours: Number(e.target.value) } })} className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
                  {[0, 12, 24, 48, 72].map((h) => <option key={h} value={h}>{h ? t("{h} Stunden", { h }) : t("sofort")}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[12px] font-medium text-fg-muted">{t("Eigener Einleitungstext (optional)")}</span>
                <textarea rows={2} className="mt-1 w-full rounded-lg border border-border bg-surface p-2.5 text-[13px]" value={s.delayNotice.intro ?? ""} placeholder={t("Standardtext verwenden")} disabled={!editable} onChange={(e) => set({ delayNotice: { ...s.delayNotice, intro: e.target.value || null } })} />
              </label>
            </div>
          )}
          {preview === "delay" && <MailPreview html={pv.data?.delay.html} subject={pv.data?.delay.subject} />}
        </Card>

        {editable && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
            <Button icon={<Send className="size-3.5" />} loading={test.isPending && test.variables === "address"} disabled={dirty} onClick={() => test.mutate("address")}>{t("Testmail Adresse")}</Button>
            <Button icon={<Send className="size-3.5" />} loading={test.isPending && test.variables === "delay"} disabled={dirty} onClick={() => test.mutate("delay")}>{t("Testmail Verzögerung")}</Button>
            {dirty && <span className="text-[12px] text-fg-subtle">{t("Erst speichern, dann testen.")}</span>}
          </div>
        )}

        <Card className="p-5">
          <h2 className="font-semibold text-[14px] flex items-center gap-2"><Mail className="size-4 text-fg-muted" /> {t("Versendete Mails")}</h2>
          {!log.data?.length ? (
            <p className="mt-2 text-[13px] text-fg-muted">{t("Noch keine Mails an eure Kunden.")}</p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-[13px] min-w-[520px]">
                <thead className="text-[12px] text-fg-subtle"><tr><th className="text-left font-medium py-1">{t("Wann")}</th><th className="text-left font-medium">{t("Art")}</th><th className="text-left font-medium">{t("An")}</th><th className="text-left font-medium">{t("Status")}</th></tr></thead>
                <tbody>
                  {log.data.map((m) => (
                    <tr key={m.id} className="border-t border-border align-top">
                      <td className="py-1.5 text-fg-muted whitespace-nowrap">{new Date(m.sentAt ?? m.createdAt).toLocaleString(intlLocale(), { dateStyle: "short", timeStyle: "short" })}</td>
                      <td><Link className="hover:underline" to={`/orders?open=${m.orderId}`}>{KIND[m.kind] ?? m.kind}</Link></td>
                      <td className="text-fg-muted break-all">{m.toEmail}</td>
                      <td><Badge tone={STATUS[m.status]?.tone ?? "neutral"}>{STATUS[m.status]?.label ?? m.status}</Badge>{(m.note || m.lastError) && <span className="block text-[11.5px] text-fg-subtle">{m.note ?? m.lastError}</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

/** Mail-Vorschau isoliert (kein Einfluss aufs Portal-Styling) */
function MailPreview({ html, subject }: { html: string | undefined; subject: string | undefined }) {
  if (!html) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <p className="px-3 py-2 text-[12px] bg-surface-2 border-b border-border"><span className="text-fg-subtle">{t("Betreff:")}</span> {subject}</p>
      <iframe title={t("Mail-Vorschau")} srcDoc={html} sandbox="" className="w-full h-[460px] bg-white" />
    </div>
  );
}

/** Logo (PNG/JPG, max. 300 KB), Hauptfarbe und Fußzeile – für Mails und die Korrekturseite */
function BrandEditor({ editable, hasLogo, color, footer, onColor, onFooter }: { editable: boolean; hasLogo: boolean; color: string | null; footer: string | null; onColor: (c: string | null) => void; onFooter: (f: string | null) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [stamp, setStamp] = useState(Date.now());
  const refresh = () => { setStamp(Date.now()); void qc.invalidateQueries({ queryKey: ["customer-comms", tenantId] }); void qc.invalidateQueries({ queryKey: ["customer-comms-preview", tenantId] }); };
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (!/^image\/(png|jpeg)$/.test(file.type)) throw new ApiError(422, { message: "Bitte ein PNG- oder JPG-Bild wählen." });
      if (file.size > 300 * 1024) throw new ApiError(422, { message: "Das Logo darf höchstens 300 KB groß sein." });
      const data = await new Promise<string>((res, rej) => { const fr = new FileReader(); fr.onload = () => res(String(fr.result)); fr.onerror = () => rej(fr.error); fr.readAsDataURL(file); });
      return api.customerComms.uploadLogo(tenantId, data);
    },
    onSuccess: () => { toast.success("Logo gespeichert"); refresh(); },
    onError: (e) => toast.error("Logo nicht gespeichert", { description: errMsg(e) }),
  });
  const remove = useMutation({ mutationFn: () => api.customerComms.deleteLogo(tenantId), onSuccess: () => { toast.success("Logo entfernt"); refresh(); } });
  const hex = color ?? "#0a0a0a";
  return (
    <div className="grid sm:grid-cols-[220px_1fr] gap-4">
      <div>
        <span className="text-[12px] font-medium text-fg-muted">{t("Logo")}</span>
        <div className="mt-1 h-20 rounded-xl border border-dashed border-border-strong grid place-items-center bg-white overflow-hidden">
          {hasLogo ? <img src={`${api.customerComms.logoUrl(tenantId)}?v=${stamp}`} alt={t("Logo")} className="max-h-14 max-w-[180px] object-contain" /> : <span className="text-[12px] text-fg-subtle">{t("kein Logo")}</span>}
        </div>
        {editable && (
          <div className="mt-2 flex gap-2">
            <label className="inline-flex"><input type="file" accept="image/png,image/jpeg" className="sr-only" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ""; }} /><span className="cursor-pointer text-[12.5px] font-medium underline underline-offset-2">{upload.isPending ? t("Lädt …") : hasLogo ? t("Ersetzen") : t("Hochladen")}</span></label>
            {hasLogo && <button type="button" className="text-[12.5px] text-fg-muted underline underline-offset-2" onClick={() => remove.mutate()}>{t("Entfernen")}</button>}
          </div>
        )}
        <p className="mt-1 text-[11.5px] text-fg-subtle">{t("PNG oder JPG, max. 300 KB. Wird in die Mail eingebettet – kein Nachladen, kein Tracking.")}</p>
      </div>
      <div className="space-y-3">
        <label className="block">
          <span className="text-[12px] font-medium text-fg-muted">{t("Hauptfarbe (Buttons, Akzent)")}</span>
          <div className="mt-1 flex items-center gap-2">
            <input type="color" value={hex} disabled={!editable} onChange={(e) => onColor(e.target.value)} className="h-9 w-12 rounded-lg border border-border bg-surface p-1" aria-label={t("Farbe wählen")} />
            <Input value={color ?? ""} placeholder="#0a0a0a" disabled={!editable} onChange={(e) => onColor(e.target.value.trim() || null)} className="w-32 font-mono" />
            <span className="h-9 px-3 rounded-lg grid place-items-center text-[12.5px] font-semibold" style={{ background: hex, color: textOn(/^#[0-9a-fA-F]{6}$/.test(hex) ? hex : "#0a0a0a") }}>{t("Adresse prüfen")}</span>
          </div>
        </label>
        <label className="block">
          <span className="text-[12px] font-medium text-fg-muted">{t("Fußzeile (Firmenangaben/Impressum)")}</span>
          <textarea rows={2} className="mt-1 w-full rounded-lg border border-border bg-surface p-2.5 text-[13px]" value={footer ?? ""} placeholder={t("Firma GmbH · Straße 1 · 12345 Ort · HRB …")} disabled={!editable} onChange={(e) => onFooter(e.target.value || null)} />
        </label>
      </div>
    </div>
  );
}
